import React, { useState, useEffect } from "react";
import Axios from "axios";
import moment from "moment-timezone";

import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";

import BillingDetailsTemplate from "./BillingDetailsTemplate";
import InvoiceTemplate_TH from "./InvoiceTemplate_TH";
import InvoiceTemplate_VN from "./InvoiceTemplate_VN";
import InvoiceTemplate_MSA from "./InvoiceTemplate_MSA";
import TechnicalReportTemplate from "./TechnicalReportTemplate";
import CreditNoteTemplate_TH from "./CreditNoteTemplate_TH";
import { sortByDate } from "../../../util/sorting";
import InvoiceTemplate_PH from "./InvoiceTemplate_PH";
import InvoiceTemplate_VN_RS_QUANTA from "./InvoiceTemplate_VN_RS_QUANTA";

function InvoicePreview(props) {
	const [selectedSite, setSelectedSite] = useState(null);
	const user = useUser();
	const onDocDefinition = props.onDocDefinition || (() => { });
	const invoiceCopyType = props.invoiceCopyType;
	const docType = props.docType;
	
	/**
	 * Return PDF preview based on selected tab
	 * @param {Selected site} selectedSite
	 */
	const renderSwitch = (selectedSite, user, isTechReport, isCreditNote) => {
		if (isCreditNote) {
			return (
				<CreditNoteTemplate_TH
					selectedSite={selectedSite}
					user={user}
					closePreview={props.closePreview}
				/>
			);
		}
		if (isTechReport) {
			return (
				<TechnicalReportTemplate
					selectedSite={selectedSite}
					user={user}
					closePreview={props.closePreview}
					isEuropeanFormat={selectedSite.table_name === "QMH_F1"}
					onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })}
				/>
			);
		}
		if (!isCreditNote && !isTechReport) {
			if (selectedSite.value.customInvTemplate) {
				if(selectedSite.value.customInvTemplate === 'RS_QUANTA') {
					return (
						<InvoiceTemplate_VN_RS_QUANTA
							selectedSite={selectedSite}
							user={user}
							closePreview={props.closePreview}
							onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })}
						/>
					);
				}
			}
			if (selectedSite.value.country === "TH") {
				if (selectedSite.value.operation_status === 2) {
					return <InvoiceTemplate_MSA selectedSite={selectedSite} user={user} closePreview={props.closePreview} onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })} copyType={invoiceCopyType} />
				}
				// All TH COD sites (including Makro) use TH invoice template
				// Billing details for Makro is a separate document type
				return (
					<InvoiceTemplate_TH
						closePreview={props.closePreview}
						selectedSite={selectedSite}
						user={user}
						onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })}
						copyType={invoiceCopyType}
					/>
				);
			} else if(selectedSite.value.country === 'PH') {
				return (
					<InvoiceTemplate_PH
						closePreview={props.closePreview}
						selectedSite={selectedSite}
						user={user}
						onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })}
						copyType={invoiceCopyType}
					/>
				)
			} else {
				return (
					<InvoiceTemplate_VN
						selectedSite={selectedSite}
						user={user}
						closePreview={props.closePreview}
						onDocDefinition={docDef => onDocDefinition({ docType, invoiceCopyType, selectedSite, docDefinition: docDef })}
					/>
				);
			}
		}
	};

	useEffect(() => {
		if (props.selectedSite == null) return;
		const tempSelected = props.selectedSite;

		async function getSiteData(site) {
			const siteData = await props.getSiteData(site);
			const dataVal = siteData.value;

			if (props.isTechReport || dataVal.company === "CP_ALL" || dataVal.company === "CPRAM") {
				const date = moment(props.selectedSite.value.date, "DD/MM/YYYY");
				const selectedMonth = date.format("YYYYMM");

				const issuesParam = {
					siteID: tempSelected.value.siteID,
					selectedMonth,
				};
				const holidaysReq = GYAxios.get(`/general_info/holidays/YYYYMM/${selectedMonth}`);
				const issuesReq = GYAxios.post("/reports/remarks", issuesParam);
				let EVNInputs = dataVal.EVNInputs;
				if (dataVal?.moreEVNInputs != null) {
					const totals = dataVal.moreEVNInputs.reduce((acc, item) => {
						return acc + item.EVN_total;
					}, 0);
					EVNInputs.EVN_total = totals;
					const ons = dataVal.moreEVNInputs.reduce((acc, item) => {
						return acc + item.EVN_on;
					}, 0);
					EVNInputs.EVN_on = ons;
					const offs = dataVal.moreEVNInputs.reduce((acc, item) => {
						return acc + item.EVN_off;
					}, 0);
					EVNInputs.EVN_off = offs;
				} 

				//Request Array
				let reqArray = [holidaysReq, issuesReq];

				await Axios.all(reqArray).then(Axios.spread((...responses) => {
					const holidaysRes = responses[0];
					const issuesRes = responses[1];

					let holidays = [];
					let holidaysDates = [];

					if (holidaysRes.data.length > 0) {
						// Sort holidays in ascending order
						let sortedHolidays = holidaysRes?.data.sort((a, b) => sortByDate(a, b, "ASC"));

						// Format and push holiday dates to holiday array
						sortedHolidays.map((holiday) => {
							if (holiday.dateData !== "-") {
								const dateNumber = moment(holiday.dateData).format("Do");
								const date = moment(holiday.dateData).format("DD-MM-YYYY");
								const holidayStr = `${dateNumber}: ${holiday.holiday_name}`;

								holidays.push(holidayStr);
								holidaysDates.push(date);
							}
						});

						// Format holiday strings
						holidays = holidays.join(", ");
					} else {
						holidaysDates = null;
						holidays = "";
					}

					let finalRes = {
						...tempSelected,
						holidays,
						holidaysDates,
						EVNInputs
					};

					if (issuesRes.data != null)
						finalRes = { ...finalRes, issues: issuesRes.data };

					setSelectedSite(finalRes)
				})).catch((err) => console.log("err :", err));
			} else if (tempSelected.value.country === "VN") {
				const isHavingModifiedTariff = dataVal.old_price != null;
				const getPriceObj = (data) => dataVal.tariffData.tariffID === "FIT" ? { priceON: data.FITOn, priceOFF: data.FITOff } : { priceON: data.priceON, priceOFF: data.priceOFF };
				try {
					const priceObj = getPriceObj(dataVal);
					const price = {
						ON: priceObj.priceON,
						OFF: priceObj.priceOFF,
						FT: dataVal.FTcost,
					};

					const discount = {
						ON: dataVal.discountON,
						OFF: dataVal.discountOFF,
						FT: dataVal.discountFT,
					};

					const pre_sub_total = isHavingModifiedTariff ? await props.calculateForChangingTariff({ table_name: siteData.table_name, contractID: dataVal.contractID, EVNInputs: dataVal.EVNInputs, old_price: dataVal.old_price, dateStr: dataVal.dateStr, FTcost: dataVal.FTcost, prices_curr: price, discount, currency: dataVal.currency, moreEVNInputs: dataVal.moreEVNInputs }) : {};
					siteData.value = { ...siteData.value, ...pre_sub_total };
					setSelectedSite(siteData);
				} catch (error) {
					console.log("Error: ", error);
					setSelectedSite({});
				}
			} else setSelectedSite(tempSelected);
		}
		getSiteData(props.selectedSite)
	}, [props.selectedSite, props.invoiceCopyType, props.isTechReport]);

	return (
		selectedSite != null && renderSwitch(selectedSite, user, props.isTechReport, props.isCreditNote)
	);
}

export default InvoicePreview;
